// BuildDateChangeBanner — the deal page's notice that a build was rescheduled
// or called off (Greg, Oct 16, 2026: "lets add a build date change banner on
// deal page"). Reads the latest build_reschedules row — written only by the
// build-calendar edge function behind Robert's Reschedule build button; RLS
// read = any signed-in staff — and shows it until the roof goes on, live via
// realtime so a change made from the PM Board lands here within a second.
//
// It also catches the sloppy path: a build date typed onto the deal AFTER a
// call-off or reschedule, without the button. The Google event, SRS and the
// homeowner were not told, and the banner says so instead of looking fine.
//
// Robert's note is internal (never sent to the homeowner); the deal page is
// staff-only, so it shows here.
//
// Oct 26, 2026: a build can run more than one day, so the dates read as a
// span ("Tue, Oct 27 – Wed, Oct 28") and a hand-typed last day counts too.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fetchRecentReschedules, fmtShortDate, fmtBuildSpan, normalizeBuildEnd, REASON_LABEL, type BuildReschedule } from "../../lib/buildCalendar";
import type { Deal } from "../../lib/types";

/** Before the build only — once the roof is on (or the deal is dead) the
 *  change is history, and the Key Dates panel already carries the dates. */
const SHOW_STAGES = new Set(["install_meeting", "build_submitted", "schedule_build", "build_scheduled"]);

type DealLite = Pick<Deal, "id" | "stage" | "build_scheduled_date" | "build_end_date" | "build_completed_date">;

function fmtWhen(ts: string): string {
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function BuildDateChangeBanner({ deal }: { deal: DealLite }) {
  const [rows, setRows] = useState<BuildReschedule[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const list = await fetchRecentReschedules(deal.id, 10);
    setRows(list);
    setLoaded(true);
    const ids = Array.from(new Set(list.map((r) => r.actor_id).filter((x): x is string => !!x)));
    if (ids.length === 0) return;
    const { data } = await supabase.from("team_roster").select("id,full_name").in("id", ids);
    if (data) setNames(Object.fromEntries((data as Array<{ id: string; full_name: string }>).map((m) => [m.id, m.full_name])));
  }, [deal.id]);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("build_reschedules", load);

  const latest = rows[0];
  if (!loaded || !latest) return null;
  if (deal.build_completed_date || !SHOW_STAGES.has(deal.stage ?? "")) return null;

  const dealDate = deal.build_scheduled_date ? deal.build_scheduled_date.slice(0, 10) : null;
  const dealEnd = dealDate ? normalizeBuildEnd(dealDate, deal.build_end_date) : null;
  const openCallOff = !latest.new_build_date && !latest.resolved_at;
  // The dates on the deal are not the ones the button set (or cleared): someone
  // typed them in by hand afterwards, so Google / SRS / the homeowner were not told.
  const handEdited = dealDate !== (latest.new_build_date ?? null)
    || (!!latest.new_build_date && dealEnd !== normalizeBuildEnd(latest.new_build_date, latest.new_build_end_date));
  const oldSpan = fmtBuildSpan(latest.old_build_date, latest.old_build_end_date);
  const newSpan = fmtBuildSpan(latest.new_build_date, latest.new_build_end_date);
  const dealSpan = fmtBuildSpan(dealDate, dealEnd);
  const reason = REASON_LABEL[latest.reason_key] ?? latest.reason_key;
  const who = latest.actor_id && names[latest.actor_id] ? names[latest.actor_id] : "";
  const when = fmtWhen(latest.created_at);
  const onSite = latest.cancelled_on_site_at ? " · cancelled on site" : "";
  const googleOk = latest.calendar_status === "updated" || latest.calendar_status === "adopted";

  const alarm = openCallOff || handEdited;
  const box = alarm
    ? "rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900"
    : "rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900";

  let headline: string;
  let detail: string;
  if (openCallOff) {
    headline = `${handEdited ? "⚠" : "⛔"} Build called off ${when} · ${reason}${latest.old_build_date ? ` · was ${oldSpan}` : ""}${onSite}${who ? ` · by ${who}` : ""}`;
    detail = handEdited
      ? `The date now on the deal (${dealSpan}) was set without the Reschedule button, so the Google event, SRS and the homeowner were not updated. Robert: 🗓 Reschedule build on the PM Board sends it out.`
      : "No new date yet. Robert sets it from the PM Board (🗓 Set new build date); the homeowner is told a new date is coming.";
  } else {
    const moved = latest.old_build_date
      ? `${oldSpan} → ${newSpan}`
      : `set for ${newSpan} after being called off`;
    headline = `${handEdited ? "⚠" : "🗓"} Build date changed ${when}: ${moved} · ${reason}${latest.new_drop_date ? ` · drop ${fmtShortDate(latest.new_drop_date)}` : ""}${onSite}${who ? ` · by ${who}` : ""}${rows.length > 1 ? ` · change ${rows.length} on this job` : ""}`;
    detail = handEdited
      ? `The deal now shows ${dealDate ? dealSpan : "no build date"}, set without the Reschedule button — the Google event, SRS and the homeowner still have ${newSpan}. Robert: 🗓 Reschedule build on the PM Board sends the real date out.`
      : "";
  }

  return (
    <div className={box}>
      <div className="font-semibold">{headline}</div>
      {detail && <div className="mt-0.5">{detail}</div>}
      <div className="mt-1 flex flex-wrap gap-1.5">
        <Chip ok={googleOk}>{googleOk ? "Google event updated" : `Google event not updated${latest.calendar_status ? ` (${latest.calendar_status.replace(/_/g, " ")})` : ""}`}</Chip>
        {latest.new_build_date && <Chip ok={latest.srs_resent}>{latest.srs_resent ? "SRS re-sent" : "SRS not re-sent"}</Chip>}
        <Chip ok={latest.homeowner_texted}>{latest.homeowner_texted ? "Homeowner texted" : "Homeowner not texted"}</Chip>
      </div>
      {latest.note && <div className="mt-1 text-[11px] text-slate-600">Note (internal): {latest.note}</div>}
      {rows.length > 1 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-[11px] text-slate-500">All {rows.length} changes on this job</summary>
          <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600">
            {rows.map((r) => (
              <li key={r.id}>
                {fmtWhen(r.created_at)} · {r.new_build_date
                  ? `${r.old_build_date ? `${fmtBuildSpan(r.old_build_date, r.old_build_end_date)} → ` : "set "}${fmtBuildSpan(r.new_build_date, r.new_build_end_date)}`
                  : `called off${r.old_build_date ? ` (was ${fmtBuildSpan(r.old_build_date, r.old_build_end_date)})` : ""}`}
                {" · "}{REASON_LABEL[r.reason_key] ?? r.reason_key}{r.actor_id && names[r.actor_id] ? ` · ${names[r.actor_id]}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Chip({ ok, children }: { ok: boolean; children: string }) {
  return (
    <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold " + (ok ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600")}>
      <span>{ok ? "✓" : "–"}</span>{children}
    </span>
  );
}
